import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  bdiAssessments: [],
  currentBdiAssessment: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all BDI Assessments (optionally by patient ID)
export const fetchBdiAssessments = createAsyncThunk(
  'bdiAssessments/fetchBdiAssessments',
  async (patientId = null, { rejectWithValue }) => {
    try {
      const url = patientId
        ? `/bdi-assessments?patient_id=${patientId}`
        : '/bdi-assessments';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch BDI assessments'
      );
    }
  }
);

// Fetch a single BDI Assessment
export const fetchBdiAssessment = createAsyncThunk(
  'bdiAssessments/fetchBdiAssessment',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/bdi-assessments/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch BDI assessment'
      );
    }
  }
);

// Create a new BDI Assessment
export const createBdiAssessment = createAsyncThunk(
  'bdiAssessments/createBdiAssessment',
  async (assessmentData, { rejectWithValue }) => {
    try {
      const response = await api.post('/bdi-assessments', assessmentData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create BDI assessment'
      );
    }
  }
);

// Update an existing BDI Assessment
export const updateBdiAssessment = createAsyncThunk(
  'bdiAssessments/updateBdiAssessment',
  async ({ case_id, assessmentData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/bdi-assessments/case/${case_id}`, assessmentData);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update BDI assessment'
      );
    }
  }
);

// Delete a BDI Assessment
export const deleteBdiAssessment = createAsyncThunk(
  'bdiAssessments/deleteBdiAssessment',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/bdi-assessments/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete BDI assessment'
      );
    }
  }
);

// ==================== Slice ====================
const bdiAssessmentsSlice = createSlice({
  name: 'bdiAssessments',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentBdiAssessment: (state, action) => {
      state.currentBdiAssessment = action.payload;
    },
    clearCurrentBdiAssessment: (state) => {
      state.currentBdiAssessment = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchBdiAssessments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBdiAssessments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.bdiAssessments = action.payload;
      })
      .addCase(fetchBdiAssessments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchBdiAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBdiAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentBdiAssessment = action.payload;
      })
      .addCase(fetchBdiAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createBdiAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createBdiAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.bdiAssessments.unshift(action.payload);
      })
      .addCase(createBdiAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateBdiAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateBdiAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload.data ?? action.payload;

        const index = state.bdiAssessments.findIndex((d) => d.id === updated.id);
        if (index !== -1) {
          state.bdiAssessments[index] = updated;
        }

        if (state.currentBdiAssessment?.id === updated.id) {
          state.currentBdiAssessment = updated;
        }
      })
      .addCase(updateBdiAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteBdiAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteBdiAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.bdiAssessments = state.bdiAssessments.filter(
          (d) => d.id !== action.payload
        );

        if (state.currentBdiAssessment?.id === action.payload) {
          state.currentBdiAssessment = null;
        }
      })
      .addCase(deleteBdiAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentBdiAssessment,
  clearCurrentBdiAssessment,
} = bdiAssessmentsSlice.actions;

export default bdiAssessmentsSlice.reducer;
